import React, {type ReactNode} from 'react';

import {emptyArray} from '@packages/utilities/fp/utils';

import {CheckboxGroupView} from './CheckboxGroup.styles';
import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';
import type {StackProps} from '../Stack';

export const CheckboxGroupContext =
  // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type 'CheckboxGroupContextType'.
  React.createContext<CheckboxGroupContextType>({});

type CheckboxGroupContextType = {
  isDisabled?: boolean;
  isError?: boolean;
  isRequired?: boolean;
  name?: string;
  values: Array<string>;
  onChangeGroup?: (
    arg1: string | undefined,
    event: React.ChangeEvent<HTMLElement>
  ) => void;
};

export type CheckboxGroupOnChangeMetaData = {
  nextIsChecked: boolean;
  nextValues: Array<string>;
  changedValue: string;
};

export type CheckboxGroupProps = Omit<HTMLAttributes, 'onChange'> & {
  /**
   * The direction the children are laid out in.
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * True disables all Checkbox descendants.
   */
  isDisabled?: boolean;
  /**
   * True marks all Checkbox descendants as errored.
   */
  isError?: boolean;
  /**
   * True marks all Checkbox descendants as required.
   */
  isRequired?: boolean;
  /**
   * The name for all Checkbox descendants.
   */
  name?: string;
  /**
   * An array of values for checked Checkboxes in a group of controlled inputs.
   */
  values?: Array<string>;
  /**
   * An array of values for checked Checkboxes in a group of uncontrolled inputs.
   */
  defaultValues?: Array<string>;
  /**
   * If no values prop is passed, onChange is a callback for when any Checkbox changes.
   * If a values prop is passed, onChange is a control handler called when any Checkbox is clicked.
   */
  onChange?: (
    event: React.ChangeEvent<HTMLElement>,
    metadata: CheckboxGroupOnChangeMetaData
  ) => void;
  /**
   * Content for CheckboxGroup.
   */
  children?: ReactNode;
} & SpringTooling &
  Pick<StackProps, 'height' | 'isFullHeight' | 'isFullWidth' | 'width'>;

/**
 * # CheckboxGroup
 *
 * Short, descriptive summary here.
 *
 * ## Usage
 *
 * ```jsx
 * import {CheckboxGroup} from '@packages/systems/spring';
 * ```
 *
 * @component
 */
export const CheckboxGroup = React.forwardRef<
  HTMLDivElement,
  CheckboxGroupProps
>(
  (
    {
      isDisabled,
      onChange,
      values: valuesProp,
      defaultValues = emptyArray as Array<string>,
      height,
      isError,
      isRequired,
      isFullHeight,
      isFullWidth,
      name,
      children,
      width,
      ...props
    }: CheckboxGroupProps,
    ref
  ) => {
    const [values, setValues] = React.useState(defaultValues);

    const onChangeGroup = (
      value: undefined | string,
      event: React.ChangeEvent<HTMLElement>
    ) => {
      const currentValues = valuesProp ?? values;
      let nextValues;

      if (!value) {
        // if no value exists for this Checkbox, nextValues is the same as current values
        nextValues = currentValues;
      } else if (currentValues.includes(value)) {
        // current value is checked, so strip it out of the nextValues array
        nextValues = currentValues.filter((_value) => _value !== value);
      } else {
        // current value is not checked, so add it to the end
        nextValues = [...currentValues, value];
      }

      if (valuesProp == null) {
        setValues(nextValues);
      }

      if (onChange) {
        onChange(event, {
          nextValues,
          // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
          nextIsChecked: nextValues.includes(value),
          changedValue: value ?? '',
        });
      }
    };

    /**
     * Map to proper Stack direction and alignment.
     */
    const align = props.orientation === 'horizontal' ? 'center' : 'stretch';
    const direction = props.orientation === 'horizontal' ? 'row' : 'column';

    return (
      <CheckboxGroupContext.Provider
        value={{
          isDisabled,
          isError,
          isRequired,
          name,
          values: valuesProp ?? values,
          onChangeGroup,
        }}
      >
        {/* @ts-expect-error - TS2322 - Typescript is having trouble with the discriminated union types here for width & isFullWidth. */}
        <CheckboxGroupView
          align={align}
          direction={direction}
          height={height}
          isFullHeight={isFullHeight}
          isFullWidth={isFullWidth}
          role="group"
          width={width}
          {...props}
          ref={ref}
        >
          {children}
        </CheckboxGroupView>
      </CheckboxGroupContext.Provider>
    );
  }
);

CheckboxGroup.displayName = 'CheckboxGroup';
CheckboxGroup.toString = CheckboxGroupView.toString;

export default CheckboxGroup;
